import React, { useEffect, useState } from "react";
import { Shield, X } from "lucide-react";
import axios from "axios";
import DOMPurify from "dompurify";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "dg_dpdp_consent";

/**
 * DPDPBanner — bottom-anchored consent strip per DPDP Act 2023 §6.
 *
 * Persists a stamped consent decision on the backend via /api/dpdp/consent
 * AND mirrors it into localStorage so we don't show the banner again to the
 * same browser unless the policy version bumps.
 *
 * Re-shows automatically when the active `policy.version` differs from the
 * one stored locally — that's how we capture re-consent on policy updates.
 */

const DPDPBanner = () => {
  const [policy, setPolicy] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [purposes, setPurposes] = useState({ whatsapp: true, marketing: false, analytics: true });
  const [submitting, setSubmitting] = useState(false);
  const [hidden, setHidden] = useState(true);

  // 1) Read stored consent + 2) fetch active policy
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/dpdp/policy-meta`).then(res => {
      if (cancelled) return;
      const active = res.data;
      setPolicy(active);
      // Prime checkbox defaults from policy meta
      const defaults = {};
      (active.purposes || []).forEach(p => {
        if (!p.required) defaults[p.key] = !!p.default_on;
      });
      setPurposes(prev => ({ ...defaults, ...prev }));
      // Decide whether to show
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const stored = raw ? JSON.parse(raw) : null;
        const samePolicy = stored && stored.policy_version === active.version;
        setHidden(!!samePolicy);
      } catch { setHidden(false); }
    }).catch(() => { /* silent — banner won't render */ });
    return () => { cancelled = true; };
  }, []);

  if (hidden || !policy) return null;

  const persist = (decision, finalPurposes) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        decision,
        purposes: finalPurposes,
        policy_version: policy.version,
        at: new Date().toISOString(),
      }));
    } catch { /* ignore */ }
  };

  const submit = async (decision) => {
    if (submitting) return;
    setSubmitting(true);
    const finalPurposes = decision === "accept"
      ? { whatsapp: true, marketing: true, analytics: true }
      : decision === "essential_only"
      ? { whatsapp: false, marketing: false, analytics: false }
      : purposes;
    persist(decision, finalPurposes);
    try {
      await axios.post(`${API_URL}/api/dpdp/consent`, {
        decision,
        purposes: finalPurposes,
        policy_version: policy.version,
        surface: showDetails ? "banner_details" : "banner",
      });
    } catch { /* fire and forget */ }
    setHidden(true);
    setSubmitting(false);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[100] bg-[#1E1B4B] text-white shadow-2xl"
      data-testid="dpdp-banner"
      role="region"
      aria-label="Cookie and data consent"
    >
      <div className="max-w-6xl mx-auto px-4 py-3">
        {!showDetails ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Shield size={18} className="text-emerald-400 flex-shrink-0" />
            <p className="flex-1 text-[13px] leading-snug">
              We use cookies and process your phone, email and address to confirm bookings, send WhatsApp updates, and (with your consent) tell you about offers.
              <button onClick={() => setShowDetails(true)} className="underline ml-1 text-emerald-300 hover:text-emerald-200" data-testid="dpdp-details-btn">
                See what & why
              </button>
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => submit("essential_only")}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white/10 hover:bg-white/20 transition disabled:opacity-50"
                data-testid="dpdp-essential-btn"
              >
                Essential only
              </button>
              <button
                onClick={() => submit("accept")}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg text-[12px] font-bold bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white shadow hover:shadow-md transition disabled:opacity-50"
                data-testid="dpdp-accept-btn"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Shield size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold mb-1">Your data choices</p>
                <p
                  className="text-[12px] text-white/75 leading-relaxed"
                  // Safe: policy.summary is DOMPurify-sanitized. The policy
                  // doc is admin-authored HTML (consent summary + links) so
                  // we need HTML rendering, but all content passes through
                  // DOMPurify.sanitize to drop scripts/event-handlers.
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(policy.summary || "") }}
                />
              </div>
              <button onClick={() => setShowDetails(false)} aria-label="Close details" className="p-1 rounded hover:bg-white/10">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-1.5 pl-7">
              {(policy.purposes || []).map(p => (
                <label key={p.key} className="flex items-start gap-2 text-[12px] cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={p.required}
                    checked={p.required || !!purposes[p.key]}
                    onChange={e => setPurposes(prev => ({ ...prev, [p.key]: e.target.checked }))}
                    className="mt-0.5 accent-[#E83E8C]"
                    data-testid={`dpdp-purpose-${p.key}`}
                  />
                  <span className={p.required ? "text-white/60" : "text-white/90"}>
                    {p.label} {p.required && <span className="text-[10px] uppercase tracking-wider opacity-60 ml-1">(required)</span>}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 pl-7 pt-1">
              <button
                onClick={() => submit("decline")}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white/10 hover:bg-white/20 transition disabled:opacity-50"
                data-testid="dpdp-decline-btn"
              >
                Decline non-essential
              </button>
              <button
                onClick={() => submit("accept")}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg text-[12px] font-bold bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white shadow hover:shadow-md transition disabled:opacity-50"
                data-testid="dpdp-save-btn"
              >
                Save my choices
              </button>
              <a
                href="/legal/privacy"
                className="text-[11px] text-emerald-300 hover:text-emerald-200 underline ml-auto"
                data-testid="dpdp-policy-link"
              >
                Full privacy policy
              </a>
            </div>
            <p className="text-[10px] text-white/50 pl-7">
              Policy v{policy.version} · effective {policy.effective_at}. Withdraw any time at <a href="/my-account" className="underline">My Account → Privacy</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DPDPBanner;
